import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { WorkflowStrip } from "@/components/WorkflowSteps";
import SetUpYourBusinessCard from "@/components/SetUpYourBusinessCard";
import AddAnotherTradeCard from "@/components/AddAnotherTradeCard";
import ChangePasswordBanner from "@/components/ChangePasswordBanner";
import BusinessTextingPrompt from "@/components/BusinessTextingPrompt";
import MeetLeviCard from "@/components/MeetLeviCard";
import LeviPrimaryHero from "@/components/levi/LeviPrimaryHero";
import { api, money, formatDate } from "@/lib/api";
import { leviPreferredName } from "@/lib/leviName";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import StatusPill from "@/components/StatusPill";
import {
  FolderKanban, FileText, Send, FileSignature, Receipt, MessageSquare, ArrowUpRight, Plus,
  ShieldCheck, Sparkles, AlertCircle, Bell,
} from "lucide-react";

const PricingReadinessBadge = ({ profile, t }) => {
  // iter59 · Slice 2A — only render the "ready" info chip here. The
  // "incomplete" state is owned by the single Set Up Your Business card
  // (SetUpYourBusinessCard) so there is exactly one contractor-facing
  // setup card on the dashboard.
  if (!profile) return null;
  const ready = profile.setup_completed === true;
  if (!ready) return null;
  const verified = profile.setup_completed_at ? formatDate(profile.setup_completed_at) : null;
  return (
    <section
      data-testid="dashboard-readiness-ready"
      className="jc-card p-4 mb-6 flex items-center justify-between gap-3 border-l-4 border-l-emerald-500"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <ShieldCheck size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[var(--jc-text)]" data-testid="dashboard-readiness-ready-title">
            {t("dashboard.pricing_profile_ready")}
          </div>
          {verified && (
            <div className="text-xs text-[var(--jc-text-soft)]" data-testid="dashboard-readiness-verified-on">
              {t("dashboard.verified_on")} {verified}
            </div>
          )}
        </div>
      </div>
      <Link
        to="/app/pricing-profile"
        data-testid="dashboard-readiness-review-link"
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
      >
        {t("dashboard.review_pricing_profile")} <ArrowUpRight size={12} />
      </Link>
    </section>
  );
};

const KPI = ({ icon: Icon, label, value, to }) => (
  <Link to={to} className="jc-card jc-card-hover p-5 flex items-start justify-between" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div>
      <div className="overline">{label}</div>
      <div className="font-display text-3xl mt-2 tracking-tight">{value}</div>
    </div>
    <div className="w-10 h-10 rounded-md bg-[var(--jc-navy)]/5 text-[var(--jc-navy)] flex items-center justify-center">
      <Icon size={18} />
    </div>
  </Link>
);

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [profile, setProfile] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  // LEVI-FIRST-01 v2 · Levi-primary hero visibility.
  //
  // Trigger: a meaningful fresh authenticated entry marked by
  // AuthContext.login() / register() writing sessionStorage
  // "jc:levi:fresh_login". Consumed exactly once on the first
  // Dashboard mount of the authenticated session so refresh, route
  // navigation, and re-opening the drawer do not replay the hero.
  //
  // Manual mode: contractor click on either the dismiss icon or the
  // "I'd rather do this manually for now" link writes
  // sessionStorage "jc:levi:manual_mode" and hides the hero for the
  // remainder of the current authenticated session. Both keys are
  // cleared by AuthContext.logout() so a new login restores the
  // Levi-primary entry.
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    try {
      const fresh = sessionStorage.getItem("jc:levi:fresh_login") === "1";
      const manual = sessionStorage.getItem("jc:levi:manual_mode") === "1";
      if (fresh && !manual) {
        setHeroVisible(true);
        // Consume the fresh-login marker immediately so subsequent
        // navigation or refresh within the same session does not
        // re-render the hero.
        sessionStorage.removeItem("jc:levi:fresh_login");
      }
    } catch { /* sessionStorage may be unavailable */ }
  }, []);

  const dismissHero = () => {
    try { sessionStorage.setItem("jc:levi:manual_mode", "1"); } catch { /* ignore */ }
    setHeroVisible(false);
  };

  useEffect(() => {
    Promise.all([
      api.get("/projects"), api.get("/estimates"), api.get("/proposals"),
      api.get("/contracts"), api.get("/invoices"), api.get("/customer-updates"),
    ]).then(([p, e, pr, c, i, u]) => {
      setProjects(p.data); setEstimates(e.data); setProposals(pr.data);
      setContracts(c.data); setInvoices(i.data); setUpdates(u.data);
    });
    api.get("/pricing-profile").then((r) => setProfile(r.data)).catch(() => {});
    // iter59 · Slice 2A — the live company profile is now the SINGLE
    // source of truth for the greeting name. Company Profile + the new
    // Set Up Your Business Step 1 both write to this same record.
    api.get("/company-profile").then((r) => setCompanyProfile(r.data)).catch(() => {});
  }, []);

  const draftEstimates = estimates.filter((e) => e.status === "draft");
  const sentProposals = proposals.filter((p) => p.status === "sent" || p.status === "approved");
  const unpaidInvoices = invoices.filter((i) => i.payment_status !== "paid");
  const activeProjects = projects.filter((p) => p.status !== "draft");
  const monthlyRevenue = invoices
    .filter((i) => i.payment_status === "paid")
    .reduce((s, i) => s + (i.amount || 0), 0);

  // V-P1 · "What Needs Your Attention" — composed strictly from
  // existing deterministic state. No new scoring, no AI, no health
  // scores, no confidence scores, no new lifecycle states. Each row is
  // sourced from data the Dashboard already loads (draft estimates,
  // sent-but-not-approved proposals, sent-but-not-signed contracts,
  // unpaid invoices, most recent customer update). If any signal is
  // absent, the corresponding row is omitted — never fabricated.
  const contractsAwaitingSignature = (contracts || []).filter(
    (c) => c.status === "sent" || c.status === "awaiting_signature"
  );
  const proposalsAwaitingResponse = (proposals || []).filter(
    (p) => p.status === "sent"
  );
  // V-P1 polish · Attention row context — surface enough identifying
  // information so rows with the same type (e.g. two draft estimates)
  // remain visually distinguishable. Pattern: `address · customer_name`
  // when both are present; falls back to whichever is available; then
  // to the original generic hint. Only fields that already exist on the
  // resource are used — no invented data, no AI scoring.
  const rowContext = (item) => {
    const addr = (item.job_address || "").trim();
    const name = (item.customer_name || "").trim();
    if (addr && name) return `${addr} · ${name}`;
    return addr || name;
  };

  const attentionItems = [
    ...draftEstimates.slice(0, 2).map((e) => ({
      key: `est-${e.id}`,
      pillLabel: t("dashboard.attention_pill_draft"),
      pillClass: "bg-amber-100 text-amber-800",
      title: t("dashboard.attention_estimate_title_generic"),
      hint: rowContext(e) || t("dashboard.attention_estimate_hint"),
      to: `/app/estimates/${e.id}`,
    })),
    ...proposalsAwaitingResponse.slice(0, 2).map((p) => ({
      key: `prop-${p.id}`,
      pillLabel: t("dashboard.attention_pill_awaiting_response"),
      pillClass: "bg-teal-100 text-teal-800",
      title: t("dashboard.attention_proposal_title_generic"),
      hint: rowContext(p) || t("dashboard.attention_proposal_hint"),
      to: `/app/proposals/${p.id}`,
    })),
    ...contractsAwaitingSignature.slice(0, 2).map((c) => ({
      key: `ctr-${c.id}`,
      pillLabel: t("dashboard.attention_pill_awaiting_signature"),
      pillClass: "bg-blue-100 text-blue-800",
      title: t("dashboard.attention_contract_title_generic"),
      hint: rowContext(c) || t("dashboard.attention_contract_hint"),
      to: `/app/contracts/${c.id}`,
    })),
    ...unpaidInvoices.slice(0, 2).map((i) => ({
      key: `inv-${i.id}`,
      pillLabel: t("dashboard.attention_pill_open_invoice"),
      pillClass: "bg-red-100 text-red-800",
      title: t("dashboard.attention_invoice_title_generic"),
      hint: (i.amount ? `${money(i.amount)} · ` : "") + (rowContext(i) || t("dashboard.attention_invoice_hint")),
      to: `/app/invoices/${i.id}`,
    })),
  ].slice(0, 6);

    // iter59 · Slice 2A — greeting falls back to the value captured at
    // signup only when the Company Profile hasn't been populated yet.
    const activeCompanyName =
    (companyProfile?.company_name && companyProfile.company_name.trim()) ||
    user?.company_name ||
    "";

  return (
    <DashboardLayout
      title={t("pages.dashboard.title", leviPreferredName(user) || t("pages.dashboard.welcome_fallback_name"))}
      subtitle={
        activeCompanyName ? (
          <span data-testid="dashboard-greeting-subtitle">
            <span data-testid="dashboard-active-company-name" className="font-semibold text-[var(--jc-text)]">
              {activeCompanyName}
            </span>
            <span className="text-[var(--jc-text-soft)]"> · AI should not replace your pricing. AI should protect your pricing.</span>
          </span>
        ) : (
          "AI should not replace your pricing. AI should protect your pricing."
        )
      }
      actions={
        <Link
          to="/app/estimates/new"
          data-testid="dashboard-new-estimate-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue"
        >
          <Plus size={14} /> <span className="hidden sm:inline">{t("dashboard.new_estimate")}</span>
        </Link>
      }
    >
      <ChangePasswordBanner />
      <BusinessTextingPrompt />
      {/* PO 2026-02 · Post-onboarding add-trade discoverability CTA.
          Deep-links into the existing Set Up Your Business → Business
          & Trades flow. Never renders alongside SetUpYourBusinessCard
          — this one only surfaces once initial trade setup is done. */}
      <AddAnotherTradeCard />
      {/* V-P1 Visual Reconciliation · LeviPrimaryHero is now the ALWAYS-VISIBLE
          Dashboard anchor. The fresh-login-only heroVisible gate and the
          permanent-dismiss behavior have been retired per PO direction. */}
      <LeviPrimaryHero />

      {/* V-P1 polish · Custom Job with Levi — compact secondary card.
          Replaces the prior full-width solid-blue gradient banner.
          White background · subtle blue accent · no generic AI/sparkle
          icon · no "Shortcut" wording. Custom Job stays useful and
          clearly secondary to the Levi hero above. */}
      <Link
        to="/app/estimates/new/levi"
        data-testid="dashboard-custom-job-shortcut"
        className="block jc-card overflow-hidden hover:shadow-md transition"
      >
        <div className="grid md:grid-cols-5 gap-0 items-stretch">
          <div className="md:col-span-2 relative bg-[var(--jc-bg)]">
            <img
              src="/brand/dashboard/custom_job_v2.png"
              alt={t("dashboard.custom_job_alt")}
              data-testid="dashboard-custom-job-img"
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>
          <div className="md:col-span-3 p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
            <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[var(--jc-blue)] mb-2">
              {t("dashboard.custom_job_eyebrow")}
            </div>
            <div className="font-display text-2xl sm:text-3xl leading-tight text-[var(--jc-text)]" data-testid="dashboard-custom-job-title">
              {t("dashboard.custom_job_title")}
            </div>
            <p className="text-sm sm:text-base text-[var(--jc-text-soft)] mt-3 leading-relaxed" data-testid="dashboard-custom-job-body">
              {t("dashboard.custom_job_body")}
            </p>
            <div className="mt-5">
              <span className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 shadow-sm">
                {t("dashboard.custom_job_cta")} <ArrowUpRight size={14} />
              </span>
            </div>
          </div>
        </div>
      </Link>

      {/* V-P1 · What Needs Your Attention. Composed strictly from
          existing draft-estimate / sent-proposal / awaiting-signature /
          unpaid-invoice signals the Dashboard already loads — no new
          scoring, no AI, no health scores, no new lifecycle states. */}
      {attentionItems.length > 0 && (
        <section data-testid="dashboard-attention-band" className="jc-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[var(--jc-blue)]" />
              <div>
                <div className="overline">{t("dashboard.attention_overline")}</div>
                <h2 className="font-display text-xl">{t("dashboard.attention_headline")}</h2>
              </div>
            </div>
            <span className="text-xs text-[var(--jc-text-soft)]">
              {t("dashboard.attention_composed_hint")}
            </span>
          </div>
          <ul className="divide-y divide-[var(--jc-border)]" data-testid="dashboard-attention-list">
            {attentionItems.map((it) => (
              <li key={it.key}>
                <Link
                  to={it.to}
                  data-testid={`attention-item-${it.key}`}
                  className="flex items-center gap-3 py-3 px-1 hover:bg-[var(--jc-bg)] rounded"
                >
                  <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${it.pillClass}`}>
                    {it.pillLabel}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{it.title}</div>
                    <div className="text-xs text-[var(--jc-text-soft)] truncate">{it.hint}</div>
                  </div>
                  <ArrowUpRight size={14} className="text-[var(--jc-blue)] shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="lg:col-span-3 jc-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">{t("dashboard.recent_projects")}</div>
              <h2 className="font-display text-xl">{t("dashboard.active_projects_h2")}</h2>
            </div>
            <Link to="/app/projects" className="text-sm text-[var(--jc-blue)] font-semibold inline-flex items-center gap-1">
              {t("dashboard.view_all")} <ArrowUpRight size={14} />
            </Link>
          </div>
          {projects.length === 0 ? (
            <EmptyProjects />
          ) : (
            /* V-P1 · Active Jobs — card grid with consistent status
                pills. Recent-first ordering by updated/created. Six
                slots on desktop (2x3), two rows on tablet. */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dashboard-active-jobs-grid">
              {[...activeProjects]
                .sort((a, b) => {
                  const at = new Date(a.updated_at || a.created_at || 0).getTime();
                  const bt = new Date(b.updated_at || b.created_at || 0).getTime();
                  return bt - at;
                })
                .slice(0, 6)
                .map((p) => (
                  <Link
                    key={p.id}
                    to={`/app/projects/${p.id}`}
                    data-testid={`dashboard-active-job-${p.id}`}
                    className="jc-card p-3 jc-card-hover group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase font-semibold tracking-wider text-[var(--jc-text-soft)] truncate">
                          {p.customer_name || t("projects_list.untitled_customer")}
                        </div>
                        <div className="font-semibold text-sm mt-0.5 truncate">
                          {p.job_type || p.job_address || t("projects_list.untitled_customer")}
                        </div>
                      </div>
                      <StatusPill status={p.status} kind="project" t={t} />
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--jc-border)]">
                      <span className="text-[10px] text-[var(--jc-text-soft)]">
                        {formatDate(p.updated_at || p.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--jc-blue)] group-hover:gap-2 transition-all">
                        {t("projects_list.open")} <ArrowUpRight size={11} />
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const EmptyProjects = () => (
  <div className="relative overflow-hidden rounded-lg border border-dashed border-[var(--jc-border-strong)] p-10 text-center">
    <div className="absolute inset-0 bp-grid opacity-50" />
    <div className="relative">
      <div className="font-display text-xl">No projects yet</div>
      <p className="text-sm text-[var(--jc-text-soft)] mt-2 max-w-sm mx-auto">
        Kick off your first project from the job walk — voice notes, photos, measurements — then let AI organize your scope.
      </p>
      <Link
        to="/app/estimates/new"
        data-testid="empty-new-estimate-btn"
        className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-md jc-btn-blue text-sm font-semibold"
      >
        <Plus size={14} /> Start a new estimate
      </Link>
    </div>
  </div>
);

export default Dashboard;
